import React, { useState } from "react";
import { useTable, List, EditButton, ShowButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Input } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { RECEIVING_QUERY } from "./queries";
import { TEAM_LIST_QUERY } from "../teams/queries";
import { Receiving, ReceivingStatus } from "./interface";

interface IReceivingRow extends Receiving {}

const StatusBadge: React.FC<{ status: ReceivingStatus }> = ({ status }) => {
  const colorMap: Record<ReceivingStatus, string> = {
    [ReceivingStatus.DRAFT]: "default",
    [ReceivingStatus.SUBMITTED_RECEIVED]: "orange",
    [ReceivingStatus.RECEIVED]: "processing",
    [ReceivingStatus.DRAFT_ACCEPTANCE]: "blue",
    [ReceivingStatus.SUBMITTED_ACCEPTANCE]: "cyan",
    [ReceivingStatus.ACCEPTED]: "success",
    [ReceivingStatus.PARTIAL_ACCEPTED]: "warning",
    [ReceivingStatus.REJECTED]: "error",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

export const ReceivingList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Teams for mapping teamId -> team name
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams =
    Array.isArray(teamsData?.data)
      ? teamsData?.data
      : (teamsData?.data as any)?.teams?.nodes || [];

  const { tableProps } = useTable<IReceivingRow>({
    resource: "receivings",
    initialSorter: [{ field: "createdAt", order: "asc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: RECEIVING_QUERY,
    },
    onSearch: (params: any) => {
      const { q } = params;
      return [
        {
          field: "receivingNo",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "vendorInfo.companyName",
          operator: "contains",
          value: q ? q : undefined,
        },
        {
          field: "poNo",
          operator: "contains",
          value: q ? q : undefined,
        },
      ];
    },
  });

  const dataSource: IReceivingRow[] = ((tableProps as any)?.dataSource || []).filter(
    (row: IReceivingRow) =>
      !searchTerm ||
      row.receivingNo?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
      row.vendorInfo?.companyName?.toLowerCase?.().includes(searchTerm.toLowerCase()) ||
      row.poNo?.toLowerCase?.().includes(searchTerm.toLowerCase())
  );

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by Receiving No, Vendor, or PO No..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 300 }}
            />
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource} rowKey="id">
        <Table.Column dataIndex="receivingNo" title="Receiving No" sorter />
        <Table.Column
          dataIndex={["vendorInfo", "companyName"]}
          title="Vendor"
          sorter
        />
        <Table.Column
          dataIndex="poNo"
          title="PO No"
          sorter
        />
        <Table.Column
          dataIndex="deliveryDate"
          title="Delivery Date"
          render={(date: string) => dayjs(date).format("YYYY-MM-DD")}
          sorter
        />
        <Table.Column
          dataIndex="totalReceivedValue"
          title="Received Value"
          sorter
          render={(v: number) => `ETB ${Number(v ?? 0).toLocaleString()}`}
        />
        <Table.Column
          dataIndex="totalAcceptedValue"
          title="Accepted Value"
          sorter
          render={(v: number) => `ETB ${Number(v ?? 0).toLocaleString()}`}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: ReceivingStatus) => <StatusBadge status={status} />}
          sorter
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IReceivingRow) => (
            <Space>
              {(record.status === ReceivingStatus.DRAFT || record.status === ReceivingStatus.RECEIVED || record.status === ReceivingStatus.DRAFT_ACCEPTANCE) && (
                <EditButton size="small" recordItemId={record.id} resource="receivings" />
              )}
              <ShowButton size="small" recordItemId={record.id} resource="receivings" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};